import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'

type ContextProps = {
  registerNestedOverlayOpen: (open: boolean) => void
  nestedOverlayOpenRef: React.RefObject<boolean>
  suppressCloseAreaClose?: boolean
  ignoreParentCloseAreaSuppression?: boolean
  cascadePreviewDepth: number
}

export const TooltipCardParentGuardContext = createContext<ContextProps>({
  registerNestedOverlayOpen: () => {},
  nestedOverlayOpenRef: { current: false },
  suppressCloseAreaClose: false,
  ignoreParentCloseAreaSuppression: false,
  cascadePreviewDepth: 0,
})

export const useTooltipCardParentGuardContext = () =>
  useContext(TooltipCardParentGuardContext)

export function TooltipCardParentGuardProvider({
  children,
  nestedOverlayOpenRef: nestedOverlayOpenRefProp,
  onNestedOverlayOpenChange,
  suppressCloseAreaClose,
  ignoreParentCloseAreaSuppression,
  withCascadePreviews = false,
}: {
  children: React.ReactNode
  nestedOverlayOpenRef?: React.MutableRefObject<boolean>
  onNestedOverlayOpenChange?: (open: boolean) => void
  suppressCloseAreaClose?: boolean
  ignoreParentCloseAreaSuppression?: boolean
  withCascadePreviews?: boolean
}) {
  const parentGuard = useTooltipCardParentGuardContext()
  const openCountRef = useRef(0)
  const internalNestedOverlayOpenRef = useRef(false)
  const nestedOverlayOpenRefPropRef = useRef(nestedOverlayOpenRefProp)
  nestedOverlayOpenRefPropRef.current = nestedOverlayOpenRefProp
  const nestedOverlayOpenRef =
    nestedOverlayOpenRefProp ?? internalNestedOverlayOpenRef

  const registerNestedOverlayOpen = useCallback(
    (open: boolean) => {
      if (open) {
        openCountRef.current += 1
      } else {
        openCountRef.current = Math.max(0, openCountRef.current - 1)
      }
      const nextOpen = openCountRef.current > 0
      internalNestedOverlayOpenRef.current = nextOpen
      if (nestedOverlayOpenRefPropRef.current) {
        nestedOverlayOpenRefPropRef.current.current = nextOpen
      }
      onNestedOverlayOpenChange?.(nextOpen)
      parentGuard.registerNestedOverlayOpen(open)
    },
    [onNestedOverlayOpenChange, parentGuard],
  )

  const value = useMemo(() => {
    return {
      registerNestedOverlayOpen,
      nestedOverlayOpenRef,
      suppressCloseAreaClose:
        suppressCloseAreaClose ?? parentGuard.suppressCloseAreaClose,
      ignoreParentCloseAreaSuppression:
        ignoreParentCloseAreaSuppression ??
        parentGuard.ignoreParentCloseAreaSuppression,
      cascadePreviewDepth:
        parentGuard.cascadePreviewDepth + (withCascadePreviews ? 1 : 0),
    }
  }, [
    registerNestedOverlayOpen,
    nestedOverlayOpenRef,
    parentGuard.suppressCloseAreaClose,
    parentGuard.ignoreParentCloseAreaSuppression,
    parentGuard.cascadePreviewDepth,
    suppressCloseAreaClose,
    ignoreParentCloseAreaSuppression,
    withCascadePreviews,
  ])

  return (
    <TooltipCardParentGuardContext.Provider value={value}>
      {children}
    </TooltipCardParentGuardContext.Provider>
  )
}
